import {Injector} from "@angular/core";
import {ReportService} from "./report.service";
import {DataResourceModel} from "../data.resource.model";
import {ReportRequestData} from "./report-request.data";
import {SavedReportData} from "./saved-report.data";
import {EntityData} from "../entity/entity.data";
import {DataClientError} from "../../error/data-client-error";

/**
 * Provides an report model and its dependencies
 * @param modelType
 * @returns {any[]}
 */
export function provideReportModel(modelType: any): any[] {
    return [
        modelType,
        {provide: ReportModel, useExisting: modelType},
        {provide: DataResourceModel, useExisting: modelType}
    ];
}

/**
 * Base class for all report models.
 */
export abstract class ReportModel<T extends EntityData<T>> extends DataResourceModel<T> {

    private reportService: ReportService;

    constructor(protected injector: Injector, dataType: typeof EntityData) {
        super(dataType);
        if (injector == null) {
            console.error("!!!! Injector not provided. This is most likely due to a missing constructor in " + this.constructor.toString());
        }

        this.reportService = injector.get(ReportService);

        this.createEntity();
    }

    /**
     * Create a new instance of the entity
     */
    private createEntity(): void {
        let entityData: EntityData<T> = EntityData.create(this.dataType);
        this.data = <T>entityData;
    }

    /**
     * Request the report using the data as the report parameters
     * @returns {Promise<ReportRequestData>}
     */
    public requestReport(): Promise<ReportRequestData> {
        return this.reportService.requestReport(this.getResourceName(), this.data);
    }

    public getDefaultSavedReportParameters(): Promise<SavedReportData> {
        return new Promise((resolve, reject) => {
            this.reportService.getDefaultSavedReportParameters(this.getResourceName(), this.getCustomReportName()).then(defaultReport => {
                if (defaultReport) {
                    const reportDefault: SavedReportData = <SavedReportData>EntityData.create(SavedReportData);
                    reportDefault.assign(defaultReport);
                    resolve(reportDefault);
                } else {
                    resolve(null);
                }
            }).catch((error: DataClientError) => {
                reject(error);
            });
        });
    }

    /**
     * Overridden by models that deal with custom reports
     */
    protected getCustomReportName(): string {
        return undefined;
    }

    public getSavedReportParameters(identifier: string): Promise<SavedReportData> {
        return new Promise((resolve, reject) => {
            this.reportService.getSavedReportParameters(this.getResourceName(), identifier).then(defaultReport => {
                if (defaultReport) {
                    const reportDefault: SavedReportData = <SavedReportData>EntityData.create(SavedReportData);
                    reportDefault.assign(defaultReport);
                    resolve(reportDefault);
                } else {
                    resolve(null);
                }
            }).catch((error: DataClientError) => {
                reject(error);
            });
        });
    }

    public getResource(): string {
        return this.getResourceName();
    }
}
